import { useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileSpreadsheet, Loader2, Upload, CheckCircle2, MailCheck } from "lucide-react";

const ACTION_META = {
  create: { label: "Will import", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  exists: { label: "Already here", cls: "border-border bg-secondary text-muted-foreground" },
  duplicate: { label: "Duplicate row", cls: "border-amber-200 bg-amber-50 text-amber-800" },
  invalid: { label: "Skipped", cls: "border-red-200 bg-red-50 text-red-700" },
};

function CountPill({ n, label, testid }) {
  return <span className="rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium tabular-nums text-foreground" data-testid={testid}>{n} {label}</span>;
}

function PreviewTable({ rows }) {
  return (
    <div className="max-h-72 overflow-auto rounded-lg border border-border">
      <Table data-testid="import-preview-table">
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead className="hidden sm:table-cell">Title · Company</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => {
            const meta = ACTION_META[r.action] || ACTION_META.invalid;
            return (
              <TableRow key={r.line} data-testid="import-preview-row" data-action={r.action}>
                <TableCell className="py-2 text-sm font-medium text-foreground">{r.name || <span className="text-muted-foreground">—</span>}{r.roles?.length ? <span className="ml-1.5 text-xs text-primary">{r.roles.join(", ")}</span> : null}</TableCell>
                <TableCell className="py-2 text-xs text-muted-foreground">{r.email || "—"}</TableCell>
                <TableCell className="hidden py-2 text-xs text-muted-foreground sm:table-cell">{[r.title, r.company].filter(Boolean).join(" · ") || "—"}</TableCell>
                <TableCell className="py-2"><Badge variant="outline" className={`whitespace-nowrap text-[11px] ${meta.cls}`} title={r.reason || ""}>{meta.label}</Badge>{r.reason && r.action === "create" && <div className="mt-0.5 text-[11px] text-muted-foreground">{r.reason}</div>}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function ImportMembersDialog({ open, onOpenChange, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [sendInvites, setSendInvites] = useState(true);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setPreview(null); setResult(null); setSendInvites(true); if (fileRef.current) fileRef.current.value = ""; };
  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const pick = async (f) => {
    if (!f) return;
    setFile(f); setPreview(null); setResult(null); setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { setPreview((await api.post("/admin/members/import/preview", fd)).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't read that CSV"); setFile(null); }
    finally { setBusy(false); }
  };

  const run = async () => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("send_invites", sendInvites ? "true" : "false");
    try {
      const r = (await api.post("/admin/members/import", fd)).data;
      setResult(r);
      toast.success(`${r.created.length} member${r.created.length === 1 ? "" : "s"} imported`);
      onImported?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const counts = result?.counts || preview?.counts;
  const inviteDays = preview?.invite_days || result?.invite_days || 14;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-2xl" data-testid="import-members-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Import members from CSV</DialogTitle>
          <DialogDescription>Bring over the legacy member list. Needs Name and Email columns; Title, Company, Bio, Interests, Roles, Points, Badges and Joined come along when present. People already on the roster are left untouched.</DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="import-file-input" />
            <button type="button" onClick={() => fileRef.current?.click()} className="flex w-full items-center gap-3 rounded-xl border border-dashed border-border bg-secondary/30 p-4 text-left transition-colors hover:border-primary hover:bg-secondary/60" data-testid="import-file-picker">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><FileSpreadsheet className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground" data-testid="import-file-name">{file ? file.name : "Choose a CSV file"}</p>
                <p className="text-xs text-muted-foreground">{file ? "Click to choose a different file" : "Exported from the old site (members_YYYY-MM-DD.csv)"}</p>
              </div>
              {busy && !preview && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </button>

            {preview && (
              <>
                <div className="flex flex-wrap gap-2">
                  <CountPill n={counts.create} label="to import" testid="import-count-create" />
                  <CountPill n={counts.exists} label="already here" testid="import-count-exists" />
                  {counts.duplicate > 0 && <CountPill n={counts.duplicate} label="duplicate rows" testid="import-count-duplicate" />}
                  {counts.invalid > 0 && <CountPill n={counts.invalid} label="skipped (bad row)" testid="import-count-invalid" />}
                </div>
                <PreviewTable rows={preview.rows} />
                <div className="flex items-start gap-3 rounded-lg border border-border bg-secondary/30 p-3">
                  <Checkbox id="send-invites" checked={sendInvites} onCheckedChange={(v) => setSendInvites(!!v)} className="mt-0.5" data-testid="import-send-invites-checkbox" />
                  <Label htmlFor="send-invites" className="cursor-pointer text-sm font-normal leading-relaxed text-foreground">
                    Email each imported member a <span className="font-medium">“set your password”</span> link ({inviteDays}-day link, one per person — {counts.invites} email{counts.invites === 1 ? "" : "s"}). Declined members never get one.
                  </Label>
                </div>
              </>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="import-result">
            <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-emerald-800">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium" data-testid="import-result-created">{result.created.length} member{result.created.length === 1 ? "" : "s"} imported.</p>
                {result.invites_queued > 0
                  ? <p className="mt-0.5 flex items-center gap-1.5" data-testid="import-result-invites"><MailCheck className="h-4 w-4" />Sending {result.invites_queued} set-password email{result.invites_queued === 1 ? "" : "s"} now (about a second apart).</p>
                  : <p className="mt-0.5">No emails sent. You can send a set-password link per member from the row menu anytime.</p>}
              </div>
            </div>
            <PreviewTable rows={result.rows} />
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => close(false)} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="import-done">Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => close(false)} data-testid="import-cancel">Cancel</Button>
              <Button onClick={run} disabled={!preview || busy || counts?.create === 0} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="import-run">
                {busy && preview ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-2 h-4 w-4" />Import {counts?.create ?? ""} member{counts?.create === 1 ? "" : "s"}</>}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
